/**
 * SubmitLakePhotoCTA — small contribution surface on each Lake Detail page.
 * Lets logged-in anglers submit a URL-based photo of the lake for review.
 *
 * Posts to POST /api/lakes/{id}/photo-submissions. Approved submissions can
 * be promoted to the lake cover by an admin, with the contributor credited.
 */
import { useState } from 'react';
import { Camera, Loader2, Check, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubmitLakePhotoCTA = ({ lakeId, lakeName, token, variant = 'card' }) => {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ image_url: '', caption: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_url.startsWith('http')) {
      toast.error('Please paste a valid photo URL (http/https).');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/lakes/${lakeId}/photo-submissions`,
        { lake_id: lakeId, image_url: form.image_url.trim(), caption: form.caption.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setDone(true);
      toast.success('Photo submitted — thank you!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit — please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setDone(false);
    setForm({ image_url: '', caption: '' });
  };

  if (variant === 'inline') {
    return (
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogTrigger asChild>
          <Button variant="ghost" size="sm" className="text-[#c8b273] hover:bg-[#c8b273]/10" data-testid="submit-lake-photo-inline">
            <Camera className="w-3.5 h-3.5 mr-1.5" />
            Submit a photo
          </Button>
        </DialogTrigger>
        <PhotoDialogBody {...{ lakeName, form, setForm, submit, submitting, done, reset }} />
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="group w-full rounded-xl border border-dashed border-[#c8b273]/30 bg-[#0b0d0b] hover:border-[#c8b273]/60 hover:bg-[#c8b273]/5 transition-all p-4 text-left flex items-center gap-3"
          data-testid="submit-lake-photo-card"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full border border-[#c8b273]/30 bg-[#c8b273]/10 text-[#c8b273] flex-shrink-0">
            <Camera className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold uppercase tracking-widest text-[10px] text-[#c8b273] mb-0.5">
              <Sparkles className="w-3 h-3 inline-block mr-1 -mt-0.5" />
              Got a great shot of this lake?
            </p>
            <p className="text-sm text-[#cfd4cf]">
              Submit a photo. Approved ones become the cover, with your credit.
            </p>
          </div>
        </button>
      </DialogTrigger>
      <PhotoDialogBody {...{ lakeName, form, setForm, submit, submitting, done, reset }} />
    </Dialog>
  );
};

const PhotoDialogBody = ({ lakeName, form, setForm, submit, submitting, done, reset }) => (
  <DialogContent className="bg-[#0b0d0b] border-white/10 max-w-md" data-testid="submit-lake-photo-dialog">
    <DialogHeader>
      <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wider text-white">
        Submit a photo of {lakeName}
      </DialogTitle>
      <DialogDescription className="text-[#a8b0a8] leading-relaxed">
        Paste a publicly-accessible URL of your photo. Our team reviews every
        submission. If approved, your photo becomes the lake cover — credited to
        your username.
      </DialogDescription>
    </DialogHeader>

    {done ? (
      <div className="rounded-xl border border-[#c8b273]/40 bg-[#c8b273]/5 p-5 flex items-start gap-3" data-testid="submit-lake-photo-success">
        <Check className="w-5 h-5 text-[#c8b273] mt-0.5 flex-shrink-0" />
        <div className="text-sm">
          <p className="font-bold uppercase tracking-widest text-[#c8b273] text-xs mb-1">Submitted for review</p>
          <p className="text-[#a8b0a8] leading-relaxed mb-3">
            We&apos;ll let you know via notification when it&apos;s reviewed. Thanks for
            helping build the community gallery!
          </p>
          <Button variant="ghost" size="sm" onClick={reset} className="text-[#c8b273] hover:bg-white/5" data-testid="submit-lake-photo-another">
            Submit another
          </Button>
        </div>
      </div>
    ) : (
      <form onSubmit={submit} className="space-y-4 mt-2">
        <div>
          <Label className="data-label text-[#a8b0a8] mb-1.5 block">Photo URL</Label>
          <Input
            type="url"
            value={form.image_url}
            onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            placeholder="https://..."
            required
            className="bg-[#141714] border-white/10 text-white placeholder:text-[#6b7d6b]"
            data-testid="submit-lake-photo-url"
          />
          <p className="text-[10px] text-[#6b7d6b] mt-1.5">
            Tip: upload to Imgur or your own hosting, then paste the direct image link.
          </p>
        </div>
        <div>
          <Label className="data-label text-[#a8b0a8] mb-1.5 block">Caption (optional)</Label>
          <Textarea
            value={form.caption}
            onChange={(e) => setForm({ ...form, caption: e.target.value })}
            placeholder="Dawn at the main lake, October 2025."
            rows={2}
            className="bg-[#141714] border-white/10 text-white placeholder:text-[#6b7d6b]"
            data-testid="submit-lake-photo-caption"
          />
        </div>
        <Button type="submit" disabled={submitting} className="btn-gold w-full" data-testid="submit-lake-photo-submit">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Camera className="w-4 h-4 mr-2" />}
          Submit for review
        </Button>
      </form>
    )}
  </DialogContent>
);

export default SubmitLakePhotoCTA;
